import { useMemo } from 'react';
import { Schedule } from '../../types';
import { useSchedules } from './useSchedules';

interface UseAccountSchedulesReturn {
  workable: Schedule[];
  rest: Schedule[];
  schedules: Schedule[] | undefined;
  primarySchedule: Schedule | null;
  additionalSchedules: Schedule[];
  hasMoreSchedules: boolean;
  isLoading: boolean;
  error: Error | null;
}

const forAccount = (schedules: Schedule[] | undefined, accountId: number) =>
  schedules?.filter((schedule) => schedule.account_id === accountId) ?? [];

export function useAccountSchedules(accountId: number): UseAccountSchedulesReturn {
  const bookable = useSchedules(true);
  const unbookable = useSchedules(false);

  const workable = useMemo(() => forAccount(bookable.data, accountId), [bookable.data, accountId]);
  const rest = useMemo(() => forAccount(unbookable.data, accountId), [unbookable.data, accountId]);

  const isLoading = bookable.isLoading || unbookable.isLoading;
  const schedules = useMemo(
    () => (isLoading ? undefined : [...workable, ...rest]),
    [isLoading, workable, rest]
  );

  const primarySchedule = workable.at(0) ?? rest.at(0) ?? null;
  const additionalSchedules = useMemo(
    () => [...workable, ...rest].filter((schedule) => schedule !== primarySchedule),
    [workable, rest, primarySchedule]
  );

  return {
    workable,
    rest,
    schedules,
    primarySchedule,
    additionalSchedules,
    hasMoreSchedules: additionalSchedules.length > 0,
    isLoading,
    error: bookable.error ?? unbookable.error,
  };
}
